import React from 'react';
import { motion } from 'framer-motion';
import { Wallet, TrendingUp, ArrowUpRight, ArrowDownLeft, Copy, Check, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageTransition from '../components/PageTransition';
import { useWallet } from '../context/WalletContext';

const WalletPage = () => {
  const {
    isConnected,
    address,
    balance,
    formatAddress,
    openModal,
    isConnecting,
    walletType
  } = useWallet();
  const [copiedAddress, setCopiedAddress] = React.useState(false);
  const [sendAmount, setSendAmount] = React.useState('');
  const [recipientAddress, setRecipientAddress] = React.useState('');

  const mockTransactions = [
    { id: 1, type: 'received', amount: '+50.00 SOL', from: '3kXt...8AsU', date: '2 hours ago' },
    { id: 2, type: 'sent', amount: '-25.50 SOL', to: '9mPq...2BvW', date: '5 hours ago' },
    { id: 3, type: 'received', amount: '+100.00 SOL', from: '4nRs...9CxY', date: '1 day ago' },
  ];

  const handleCopyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  // Not connected state
  if (!isConnected) {
    return (
      <PageTransition>
        <div className="min-h-screen text-white bg-[#0A0A0A] pt-20 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-dark rounded-3xl p-12 max-w-md w-full text-center"
          >
            <img
              src="/dobix-logo.png"
              alt="Dobix"
              className="h-24 w-24 mx-auto mb-6"
            />
            <h2 className="text-3xl font-bold mb-4 font-heading">
              Connect Your Wallet
            </h2>
            <p className="text-gray-400 mb-8">
              Connect your wallet to start using Dobix Coin features.
            </p>
            <button
              onClick={openModal}
              disabled={isConnecting}
              className="w-full btn-primary flex items-center justify-center gap-2 py-4"
            >
              <Wallet className="w-5 h-5" />
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Supports MetaMask, Phantom, Coinbase and more
            </p>
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  // Connected state
  return (
    <PageTransition>
      <div className="min-h-screen text-white bg-[#0A0A0A] pt-20">
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl font-bold mb-2 font-heading">
              Your <span className="text-gradient">Wallet</span>
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 card-dark px-4 py-2 rounded-lg">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-sm text-gray-400 capitalize">{walletType}</span>
              </div>
              <code className="text-gray-400 text-sm card-dark px-4 py-2 rounded-lg">{address}</code>
              <button
                onClick={handleCopyAddress}
                className="text-[#FF6B00] hover:text-[#FFB800] transition-colors"
              >
                {copiedAddress ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </motion.div>

          {/* Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="stat-card mb-8 glow-orange"
          >
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <p className="text-gray-400 text-sm mb-2">Total Balance</p>
                <h2 className="text-5xl font-bold text-gradient mb-2">
                  {balance || '0.00'} <span className="text-2xl text-gray-400">{walletType === 'phantom' ? 'SOL' : 'ETH'}</span>
                </h2>
                <p className="text-xl text-gray-300">≈ $0.00 USD</p>
              </div>
              <div className="flex items-center gap-2 text-green-400 card-dark px-4 py-2 rounded-lg">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold">+0.00%</span>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Send/Receive */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="send" className="w-full">
                <TabsList className="grid w-full grid-cols-2 card-dark mb-4 p-1 rounded-xl">
                  <TabsTrigger value="send" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF6B00] data-[state=active]:to-[#FFB800] data-[state=active]:text-white rounded-lg py-3">
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    Send
                  </TabsTrigger>
                  <TabsTrigger value="receive" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF6B00] data-[state=active]:to-[#FFB800] data-[state=active]:text-white rounded-lg py-3">
                    <ArrowDownLeft className="w-4 h-4 mr-2" />
                    Receive
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="send">
                  <div className="card-dark rounded-2xl p-6">
                    <h3 className="text-xl font-bold mb-6">Send Crypto</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-2 block">Recipient Address</label>
                        <Input
                          value={recipientAddress}
                          onChange={(e) => setRecipientAddress(e.target.value)}
                          placeholder="Enter wallet address"
                          className="bg-[#1a1a1a] border-[#333333] text-white rounded-xl py-3"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400 mb-2 block">Amount</label>
                        <Input
                          type="number"
                          value={sendAmount}
                          onChange={(e) => setSendAmount(e.target.value)}
                          placeholder="0.00"
                          className="bg-[#1a1a1a] border-[#333333] text-white rounded-xl py-3"
                        />
                      </div>
                      <button className="w-full btn-primary py-4">
                        Send Transaction
                      </button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="receive">
                  <div className="card-dark rounded-2xl p-6">
                    <h3 className="text-xl font-bold mb-6">Receive Crypto</h3>
                    <div className="text-center">
                      <div className="bg-white p-4 rounded-2xl inline-block mb-4">
                        <div className="w-40 h-40 bg-gray-100 rounded-xl flex items-center justify-center">
                          <ExternalLink className="w-10 h-10 text-gray-400" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-400 mb-2">Your Wallet Address</p>
                      <div className="flex items-center justify-center gap-2 card-dark rounded-xl p-3">
                        <code className="text-sm break-all">{address}</code>
                        <button
                          onClick={handleCopyAddress}
                          className="text-[#FF6B00] hover:text-[#FFB800] flex-shrink-0"
                        >
                          {copiedAddress ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Transactions */}
              <div className="card-dark rounded-2xl p-6 mt-6">
                <h3 className="text-xl font-bold mb-6">Recent Transactions</h3>
                <div className="space-y-3">
                  {mockTransactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between p-4 rounded-xl hover:bg-[#1a1a1a] transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'received' ? 'bg-green-500/20' : 'bg-red-500/20'
                          }`}>
                          {tx.type === 'received' ? (
                            <ArrowDownLeft className="w-5 h-5 text-green-400" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{tx.type === 'received' ? 'Received' : 'Sent'}</p>
                          <p className="text-sm text-gray-400">{tx.from || tx.to}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${tx.type === 'received' ? 'text-green-400' : 'text-red-400'}`}>
                          {tx.amount}
                        </p>
                        <p className="text-sm text-gray-400">{tx.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Sidebar */}
            <div className="space-y-6">
              <div className="card-dark rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Total Sent</p>
                    <p className="text-2xl font-bold text-red-400">-100.75 SOL</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Total Received</p>
                    <p className="text-2xl font-bold text-green-400">+150.00 SOL</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Transactions</p>
                    <p className="text-2xl font-bold text-gradient">247</p>
                  </div>
                </div>
              </div>

              <div className="card-dark rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-4">Network</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">Status</span>
                    <span className="text-sm text-green-400 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      Online
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-400">TPS</span>
                    <span className="text-sm text-[#FF6B00]">2,847</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default WalletPage;